// PostHog product analytics — thin, safe wrapper.
//
// Every function is a no-op unless REACT_APP_POSTHOG_KEY is set at build time,
// so local dev, tests, and CI stay silent by default. Turn it on by dropping
// the key into client/.env.local (gitignored):
//
//   REACT_APP_POSTHOG_KEY=phc_xxx
//   REACT_APP_POSTHOG_HOST=https://eu.i.posthog.com   # optional, EU is default
//
// Design notes:
//   - Users are identified by numeric id, with email + role as person props so
//     actions are attributable to a real person in PostHog. No invoice or
//     financial data (amounts, supplier names, doc content) ever leaves here.
//   - Restaurants are modeled as a PostHog "group" (tenant), carrying the
//     restaurant name, so behavior can be sliced per restaurant/chain — the
//     shape future AI analysis will want.
//   - The restaurant is attached to EACH event from this tab's own tenant
//     (the URL), never from PostHog's persisted super properties:
//     those live in localStorage, shared by every tab, so a second tab on
//     another restaurant would re-file this tab's events under its own (#179).
//   - Session recording is disabled: screens contain supplier financials.
//   - posthog-js is loaded LAZILY via dynamic import() so its ~77KB (gzip)
//     lands in its own chunk instead of the critical-path main bundle — no
//     effect on first paint (important on mobile/slow networks). Events fired
//     before the chunk resolves are buffered and replayed in order, so nothing
//     is lost. This is identical on desktop and mobile browsers.

import { parseTenantPath } from './tenantPath';

// Canonical event names. Use these everywhere instead of raw strings so the
// taxonomy stays stable and queryable (matters for later automated analysis).
export const EVENTS = {
  CATEGORY_FOOD_COST_TOGGLED: 'category_food_cost_toggled',
  // Auth / session
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  RESTAURANT_SWITCHED: 'restaurant_switched',

  // Upload → extract → save (front of the funnel)
  DOCUMENT_EXTRACTED: 'document_extracted',            // AI/manual OCR of upload finished
  DOCUMENT_EXTRACT_FAILED: 'document_extract_failed',
  DOCUMENT_SAVED: 'document_saved',                    // draft persisted to server
  BULK_UPLOAD_COMPLETED: 'bulk_upload_completed',    // { saved, failed, duplicates, partial } — partial = stored without an AI read, saved as empty drafts

  // Extraction quality & upload friction
  AI_EXTRACTION_CORRECTED: 'ai_extraction_corrected',  // flagship: human fixed the AI parse
  AI_EXTRACTION_SKIPPED: 'ai_extraction_skipped',      // user bypassed AI extraction
  DOCUMENT_SAVE_BLOCKED: 'document_save_blocked',      // a validation guard stopped the save
  SUPPLIER_UNMATCHED: 'supplier_unmatched',            // extracted supplier had no match
  SUPPLIER_CREATED_INLINE: 'supplier_created_inline',
  RECIPIENT_MISMATCH_SHOWN: 'recipient_mismatch_shown',// doc may be in the wrong tenant
  REVIEW_ABANDONED: 'review_abandoned',                // left review stage without saving
  EXTRACT_RETRIED: 'extract_retried',                  // retried after an extract failure

  // Funnel completion (approve → accounting)
  DOCUMENT_APPROVED: 'document_approved',
  // דלג on the review screen (#260): { doc_type, saved_changes }. The draft
  // stays טיוטה; no reason is asked for.
  DRAFT_SKIPPED: 'draft_skipped',
  // The next draft of the review queue ready (#315): { ready_ms, cache }.
  // ready_ms runs from the moment the page starts moving to the next draft —
  // after אשר והבא's PUT and its follow-ups (payment, link offers), or at
  // דלג / מחק והבא — to that draft's form being up, the bounded wait
  // included; not the decision's own save. cache = warm / in_flight / none,
  // what the prefetch had ready on arrival (utils/draftPrefetch.js).
  DRAFT_READY: 'draft_ready',
  // /drafts' gate filter toggled (#332): { filter: 'red' | 'yellow', on,
  // count, queue_size }. document_approved carries gate_color (red / yellow /
  // green, or null when no mark was shown — pending, held out, bulk) and
  // gate_shown.
  GATE_FILTER_USED: 'gate_filter_used',
  // A soft delete (server/lib/softDelete.js): { reason, doc_type, was_draft,
  // source }. Enums only — reason is the key (duplicate / wrong_restaurant /
  // unreadable / other), never the free-text note.
  DOCUMENT_DELETED: 'document_deleted',
  // A Command Center restore: { doc_type, restored_as_draft, days_deleted }.
  DOCUMENT_RESTORED: 'document_restored',
  // A draft moved to another restaurant (#304): { matched_by: 'bid' | 'name'
  // | null (how the recipient pointed there; null = picked by search),
  // suggested (the pre-selected restaurant was taken), supplier_matched }.
  // No names, no amounts.
  DOCUMENT_MOVED: 'document_moved',
  DOCUMENT_FORWARDED_TO_DOKKA: 'document_forwarded_to_dokka',
  // The original scan opened on /document/:id, carrying { device, trigger:
  // 'auto' (arrived via צפה במסמך) | 'manual' }. Answers "who actually looks
  // at the paper, and on what" — the question the 2026-09 page redesign had
  // no data for.
  DOCUMENT_FILE_OPENED: 'document_file_opened',

  // Reconciliation, credits, payments (retention modules)
  DELIVERY_NOTES_LINKED: 'delivery_notes_linked',
  OPEN_CREDITS_LINKED: 'open_credits_linked',
  CREDIT_STATUS_CHANGED: 'credit_status_changed',
  // Deliberate click on the פתוחים / היסטוריית זיכויים view tabs — same
  // fired-only-on-genuine-switch rule as PAYMENT_VIEW_TAB_CLICKED below.
  CREDIT_VIEW_TAB_CLICKED: 'credit_view_tab_clicked',
  // One per mount of either credits view, carrying { view, entry }. Mirrors
  // PAYMENT_VIEW_SHOWN's reasoning: TAB_CLICKED alone misses bookmark/refresh/
  // back-button arrivals.
  CREDIT_VIEW_SHOWN: 'credit_view_shown',
  PAYMENT_RECORDED: 'payment_recorded',
  BULK_PAYMENT_RECORDED: 'bulk_payment_recorded',
  // Deliberate click on the לתשלום / שולם view tabs. Fired ONLY from the tab's
  // own onClick — never on mount, route restore, or a click on the already-
  // active tab — so the count is real user intent, not navigation noise.
  PAYMENT_VIEW_TAB_CLICKED: 'payment_view_tab_clicked',
  // One per mount of either view, carrying { view, entry: 'tab_click'|'direct' }.
  // TAB_CLICKED alone measures switching intent and misses anyone arriving by
  // bookmark, refresh or back button — which undercounts real usage of the
  // שולם view. This is the "which view do people actually use" metric.
  PAYMENT_VIEW_SHOWN: 'payment_view_shown',
  SUPPLIER_MONTH_APPROVED: 'supplier_month_approved',  // per-supplier monthly sign-off

  // Dish costing (עץ מוצר). One per visit to the module's list screen — the
  // adoption metric for a feature that is rolled out one restaurant at a time
  // and is invisible to viewers, so "who opened it, and from which restaurant"
  // is the question. Both ride along automatically: identifyUser sets the
  // person (id + email + role) and track() attaches this tab's restaurant_id
  // and restaurant_name to every event, so this fires with no payload of its
  // own beyond how the user got here.
  DISH_LIST_VIEWED: 'dish_list_viewed',
  // Usage, not just arrival. VIEWED answers "who opened it"; these three answer
  // "who actually built something with it", which is the difference between a
  // module that shipped and a module that is used.
  DISH_CREATED: 'dish_created',
  DISH_INGREDIENT_ADDED: 'dish_ingredient_added',
  DISH_PRICE_ALERTS_REVIEWED: 'dish_price_alerts_reviewed',

  // Procurement analysis (ניתוח רכש) & reports
  ANALYSIS_VIEWED: 'analysis_viewed',
  NETWORK_REPORT_VIEWED: 'network_report_viewed',
  NETWORK_DRILLED: 'network_drilled',                  // expanded supplier/branch/products
  NETWORK_PRICING_EDITED: 'network_pricing_edited',    // set network catalog price / discount
  ANALYSIS_EXPORTED: 'analysis_exported',
  PRICE_CHANGES_REVIEWED: 'price_changes_reviewed',
  // DEMAND PROBE, not a feature event. There is no detailed budget report yet,
  // and the owner's decision is to measure who asks for one before building it.
  // The לצפייה footer on רכש מול יעדי תקציב was inert for exactly that reason;
  // it is now a real button whose only job is to fire this and say "soon".
  // Read it as "how many people wanted the report" — so it must stay on the
  // CLICK, never on the sheet rendering, or a re-render would inflate demand.
  BUDGET_REPORT_INTEREST: 'budget_report_interest',

  // Data hygiene / manager admin
  SUPPLIERS_MERGED: 'suppliers_merged',
  UNPRICED_ITEM_PRICED: 'unpriced_item_priced',
  PAYMENT_TERMS_CHANGED: 'payment_terms_changed',
  // The WIRE VALUE stays 'category_tagged' even though the concept was renamed
  // to item classification. These strings leave the app and land in PostHog,
  // where saved dashboards, funnels and retention queries filter on them by
  // name: renaming the value would make every one of them silently report zero
  // from the deploy onward, with no error and no way to join the history to the
  // new data. Rename the CONSTANT freely; the string is a published contract.
  CLASSIFICATION_TAGGED: 'category_tagged',
};

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let enabled = false;   // key present + production: we intend to send
let ph = null;         // the lazily-loaded posthog-js module
let ready = false;     // ph loaded + init'd
const pending = [];    // ops fired before ph finished loading

// Run `fn(ph)` now if posthog is ready, else buffer it for replay after load.
// Keeps the queue bounded so a misconfigured load can't grow memory unbounded.
function whenReady(fn) {
  if (ready) fn(ph);
  else if (pending.length < 500) pending.push(fn);
}

// Call once at app boot. Safe to call more than once.
//
// Guarded so analytics ONLY runs in a production build. `npm start`
// (NODE_ENV==='development') stays silent even with the key in .env.local, so
// local dev/QA clicks never pollute product analytics — real events come only
// from the deployed build. CRA inlines NODE_ENV at build time.
//
// posthog-js is imported dynamically so it becomes its own async chunk (kept
// out of the main bundle / first paint). Events queued before it resolves are
// replayed in order once ready.
export function initAnalytics() {
  if (enabled || !KEY || process.env.NODE_ENV !== 'production') return;
  enabled = true;
  import('posthog-js')
    .then(({ default: loaded }) => {
      ph = loaded;
      ph.init(KEY, {
        api_host: HOST,
        // We fire $pageview manually on route change (SPA) — see trackPageview.
        capture_pageview: false,
        // Screens show supplier invoices / financials: never record them.
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
        // Autocapture is OFF on purpose. It would send the visible TEXT of
        // clicked buttons/rows (e.g. a "פתח זיכוי ₪123" button, or a table row
        // containing a supplier name + amount) as $el_text — leaking exactly
        // the financial/PII data this app must not emit. Our explicit
        // track(EVENTS.*) calls provide the full taxonomy without that risk.
        autocapture: false,
        // Dead-click autocapture captures clicked-element context the same way
        // autocapture does — off for the same privacy reason. (web-vitals /
        // surveys are controlled by PostHog project settings, not here.)
        capture_dead_clicks: false,
      });
      // Clear legacy persisted tenant props before replaying the first pageview.
      ph.unregister('restaurant_id');
      ph.unregister('restaurant_name');
      ready = true;
      while (pending.length) pending.shift()(ph);
    })
    .catch(() => { enabled = false; pending.length = 0; });
}

// Tie subsequent events to a user (numeric id) and their active tenant.
// email/role live on the PERSON (via identify) so a user is attributable in the
// Persons view; role is ALSO registered as a super property so it shows on every
// event. restaurantName enriches the tenant group when known.
export function identifyUser(user, restaurantId, restaurantName) {
  if (!enabled || !user?.id) return;
  whenReady(p => {
    p.identify(String(user.id), { email: user.email, role: user.role });
    if (user.role) p.register({ role: user.role });
  });
  // Called unconditionally: a null restaurantId still clears what older
  // builds registered.
  setRestaurantGroup(restaurantId, restaurantName);
}

// This tab's restaurant name, remembered WITH its id so a name can never be
// attached to a different restaurant. sessionStorage (per tab) so the first
// event after a reload — the pageview, fired before the restaurant list has
// loaded — still carries it.
const TENANT_NAME_KEY = 'peak.analyticsTenantName';

function rememberTenantName(id, name) {
  try { window.sessionStorage.setItem(TENANT_NAME_KEY, JSON.stringify({ id: String(id), name })); }
  catch { /* storage unavailable: events carry the id without the name */ }
}

function tenantNameFor(id) {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(TENANT_NAME_KEY));
    return stored && stored.id === id ? stored.name : undefined;
  } catch { return undefined; }
}

// The restaurant properties for an event fired from THIS tab, right now.
// `$groups` is passed explicitly — also when empty — because posthog.group()
// persists the group in shared storage, and event properties override it.
function tenantProps() {
  const id = parseTenantPath(window.location.pathname).rid;
  if (id == null) return { $groups: {} };
  const name = tenantNameFor(id);
  return {
    $groups: { restaurant: id },
    restaurant_id: id,
    ...(name ? { restaurant_name: name } : {}),
  };
}

// Tell PostHog about the restaurant this tab is on: name the tenant group and
// remember the name for tenantProps(). The id itself is NOT taken from here —
// every event reads it from the tab (tenantProps), so there is no second copy
// to drift. Pass null when no restaurant is selected.
export function setRestaurantGroup(restaurantId, restaurantName) {
  if (!enabled) return;
  // Builds before #179 registered these as super properties, which persist in
  // localStorage and are shared across tabs. Drop what they left behind, or an
  // event with no restaurant would still be filed under the last one.
  whenReady(p => { p.unregister('restaurant_id'); p.unregister('restaurant_name'); });
  if (restaurantId == null) return;
  if (restaurantName) rememberTenantName(restaurantId, restaurantName);
  whenReady(p => {
    p.group('restaurant', String(restaurantId), restaurantName ? { name: restaurantName } : undefined);
  });
}

// Clear identity + super properties on logout so the next user starts clean.
// posthog.reset() drops the distinct_id AND all registered super properties.
export function resetAnalytics() {
  if (!enabled) return;
  try { window.sessionStorage.removeItem(TENANT_NAME_KEY); } catch { /* nothing to clear */ }
  whenReady(p => p.reset());
}

// Fire a named product event. props should be small, non-PII, non-financial.
// The restaurant is read NOW, not when a buffered call replays: by then the
// tab may be on another restaurant.
export function track(event, props) {
  if (!enabled) return;
  const merged = { ...tenantProps(), ...props };
  whenReady(p => p.capture(event, merged));
}

// Fire a SPA pageview for the current route.
export function trackPageview(path) {
  if (!enabled) return;
  const merged = { ...tenantProps(), ...(path ? { $current_url: path } : {}) };
  whenReady(p => p.capture('$pageview', merged));
}
